import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiX, FiGift } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { getLoginOffer } from "../../api/couponApi";
import "./LoginOfferPopup.css";

const SHOW_AFTER_MS = 6000;
const SNOOZE_MS = 24 * 60 * 60 * 1000; // after "Maybe later", ask again tomorrow
const STORAGE_KEY = "login_offer_dismissed_at";
const SESSION_KEY = "login_offer_shown";

// Pages where nagging a visitor to log in makes no sense.
const HIDDEN_ON = ["/login", "/checkout", "/account", "/order-confirmation", "/confirm-order", "/admin", "/adminLogin", "/adminDashboard", "/consultation/confirmation"];

const safeGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode) - the popup just may repeat */
  }
};

const inr = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const headline = (offer) =>
  offer.discountType === "percentage"
    ? `${Number(offer.discountValue)}% OFF`
    : `${inr(offer.discountValue)} OFF`;

// "Login here to get 5-10% off at checkout" pop-up for visitors who aren't
// signed in. The discount itself is a login-only coupon the admin controls.
export default function LoginOfferPopup() {
  const { isLoggedIn, initializing } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const loginRef = useRef(null);

  const hiddenHere = HIDDEN_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const eligible = !initializing && !isLoggedIn && !hiddenHere;

  // Fetch the offer only for people who could actually see it.
  useEffect(() => {
    if (!eligible || offer) return undefined;
    let cancelled = false;
    getLoginOffer().then((result) => {
      if (!cancelled && result?.success && result.data) setOffer(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [eligible, offer]);

  // Show once per visit, after a short delay, unless dismissed in the last 24h.
  useEffect(() => {
    if (!eligible || !offer || open) return undefined;
    if (safeGet(sessionStorage, SESSION_KEY)) return undefined;
    const dismissedAt = Number(safeGet(localStorage, STORAGE_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < SNOOZE_MS) return undefined;

    const timer = setTimeout(() => {
      setOpen(true);
      safeSet(sessionStorage, SESSION_KEY, "1");
    }, SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [eligible, offer, open]);

  const close = useCallback(() => {
    setOpen(false);
    safeSet(localStorage, STORAGE_KEY, String(Date.now()));
  }, []);

  // A visitor who logs in (or lands on a hidden page) shouldn't keep it open.
  useEffect(() => {
    if (open && !eligible) setOpen(false);
  }, [open, eligible]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    loginRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  if (!open || !offer) return null;

  const goLogin = () => {
    setOpen(false);
    navigate("/login", { state: { redirectTo: `${pathname}` } });
  };

  const conditions = [];
  if (offer.discountType === "percentage" && offer.maxDiscountAmount) {
    conditions.push(`up to ${inr(offer.maxDiscountAmount)}`);
  }
  if (Number(offer.minOrderAmount) > 0) {
    conditions.push(`on orders above ${inr(offer.minOrderAmount)}`);
  }

  return (
    <div className="lo-overlay" onClick={close}>
      <div
        className="lo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lo-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="lo-close" onClick={close} aria-label="Close">
          <FiX />
        </button>

        <span className="lo-icon" aria-hidden="true">
          <FiGift />
        </span>

        <p className="lo-eyebrow">Welcome offer</p>
        <h2 id="lo-title">
          Login here to get <strong>{headline(offer)}</strong>
        </h2>
        <p className="lo-text">
          at checkout{conditions.length > 0 ? `, ${conditions.join(", ")}` : ""}. Log in with your mobile number
          and the discount is applied to your order automatically.
        </p>
        <p className="lo-note">One coupon can be used per order.</p>

        <button type="button" className="lo-btn lo-btn-gold" ref={loginRef} onClick={goLogin}>
          Login / Sign up
        </button>
        <button type="button" className="lo-btn lo-btn-ghost" onClick={close}>
          Maybe later
        </button>
      </div>
    </div>
  );
}
